import clsx from 'clsx';
import { motion } from 'motion/react';
import * as React from 'react';
import { mergeProps, useFocusRing, usePress } from 'react-aria';

import { Icon } from '@gitbook/spine-icons';
import { useEventCallback, useResizeObserver } from '@gitbook/util-react-hooks';
import { assert } from '@gitbook/util-ts';

/**
 * Stepper component that displays progress through a sequence of numbered steps.
 */
export function Stepper<StepId extends string>(props: {
    children: React.ReactNode;
    value: StepId;
    onChange: (value: StepId) => void;
    disabled?: boolean;
    className?: string;
}) {
    const steps = React.Children.toArray(props.children).filter(
        (child): child is React.ReactElement<StepperStepProps> => {
            if (!React.isValidElement(child)) {
                return false;
            }

            if (child.type !== StepperStep) {
                throw new Error('Stepper only accepts StepperStep children');
            }

            return true;
        }
    );
    const stepCount = steps.length;
    const bulletRefs = React.useMemo(
        () => Array.from({ length: stepCount }).map(() => React.createRef<HTMLDivElement>()),
        [stepCount]
    );
    const [stepX, setStepX] = React.useState<Array<number>>([]);
    const activeStepIndex = steps.findIndex((step) => step.props.id === props.value);
    const containerRef = useResizeObserver(() => {
        setStepX(
            bulletRefs.map((ref) => {
                assert(ref.current);
                return ref.current.offsetLeft + ref.current.offsetWidth / 2;
            })
        );
    });

    // Recompute stepX when bullets change, so that lines are up to date as well.
    // This is necessary for steppers with variable number of steps. E.g. steps
    // affecting the visibility of following steps, like in the end-of-trial flow.
    React.useEffect(() => {
        setStepX(
            bulletRefs.map((ref) => {
                if (ref.current) {
                    return ref.current.offsetLeft + ref.current.offsetWidth / 2;
                }
                return 0;
            })
        );
    }, [bulletRefs]);

    if (steps.length === 0) {
        return null;
    }

    const lineStyle = (() => {
        const first = stepX[0];
        const last = stepX[stepX.length - 1];
        if (first === undefined || last === undefined) {
            return null;
        }
        return { left: first, width: last - first };
    })();
    const activeLineStyle = (() => {
        const first = stepX[0];
        const last = stepX[activeStepIndex];
        if (first === undefined || last === undefined) {
            return null;
        }
        return { left: first, width: last - first };
    })();

    return (
        <div
            ref={containerRef}
            className={clsx('relative z-10 flex w-full justify-between gap-6', props.className)}
        >
            {steps.map((step, index) => {
                return (
                    <StepContainer
                        key={step.props.id}
                        index={index}
                        active={index === activeStepIndex}
                        completed={index < activeStepIndex}
                        onChange={props.onChange}
                        bulletRef={bulletRefs[index]}
                        disabled={props.disabled ?? false}
                    >
                        {step}
                    </StepContainer>
                );
            })}
            {lineStyle && (
                <div
                    className="-z-[1] absolute top-[calc(0.75rem-1px)] h-[2px] bg-secondary"
                    style={lineStyle}
                />
            )}
            {activeLineStyle && (
                <motion.div
                    initial={false}
                    animate={{ width: activeLineStyle.width }}
                    className="-z-[1] absolute top-[calc(0.75rem-1px)] h-[2px] bg-action-primary"
                    style={{ left: activeLineStyle.left }}
                    transition={{ duration: 0.2 }}
                />
            )}
        </div>
    );
}

function StepContainer(props: {
    index: number;
    active: boolean;
    completed: boolean;
    children: React.ReactNode;
    onChange: (value: string) => void;
    bulletRef: React.RefObject<HTMLDivElement>;
    disabled: boolean;
}) {
    const onChange = useEventCallback(props.onChange);
    const value = React.useMemo<StepperStepContextType>(
        () => ({
            active: props.active,
            completed: props.completed,
            index: props.index,
            onChange,
            bulletRef: props.bulletRef,
            disabled: props.disabled,
        }),
        [onChange, props.active, props.completed, props.index, props.bulletRef, props.disabled]
    );
    return (
        <StepperStepContext.Provider value={value}>{props.children}</StepperStepContext.Provider>
    );
}

export type StepperStepContextType = {
    index: number;
    active: boolean;
    completed: boolean;
    onChange: (value: string) => void;
    bulletRef: React.RefObject<HTMLDivElement>;
    disabled: boolean;
};

const StepperStepContext = React.createContext<StepperStepContextType | null>(null);

export type StepperStepProps = { id: string; title: React.ReactNode };

export function StepperStep(props: StepperStepProps) {
    const { title } = props;
    const ctx = React.useContext(StepperStepContext);
    assert(ctx, 'StepperStep must be wrapped in a Stepper component');
    const { index, active, completed, bulletRef, disabled } = ctx;
    const pressable = completed && !disabled;
    const { pressProps } = usePress({
        onPress: () => {
            ctx.onChange(props.id);
        },
        isDisabled: !pressable,
    });
    const { focusProps, isFocusVisible } = useFocusRing();
    return (
        <div
            className={clsx(
                'group flex flex-col items-center gap-1 rounded-sm',
                pressable && 'cursor-pointer',
                isFocusVisible && pressable && 'ring-primary ring-offset-2 focus:ring-2'
            )}
            {...(pressable && {
                role: 'button',
                tabIndex: 0,
                ...mergeProps(pressProps, focusProps),
            })}
        >
            <div
                ref={bulletRef}
                className={clsx(
                    'flex size-6 shrink-0 items-center justify-center rounded-full border text-ui-small tabular-nums transition',
                    active
                        ? 'border-primary bg-base text-primary delay-100'
                        : completed
                          ? clsx(
                                'border-primary bg-action-primary text-inverted',
                                pressable && 'group-hover:bg-action-primary-hover'
                            )
                          : 'border-[--sp-color-bg-secondary] bg-secondary text-muted'
                )}
            >
                {completed ? <Icon.Check className="text-[1.2em]" /> : index + 1}
            </div>
            <div
                className={clsx(
                    'text-center text-ui-small',
                    active
                        ? 'text-primary'
                        : completed
                          ? clsx('text-muted', pressable && 'group-hover:text-primary')
                          : 'text-muted'
                )}
            >
                {title}
            </div>
        </div>
    );
}
